import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { getHeader, replaceUhfHeaderLogoInHeaderHtml } from "@/services/uhfService";
import { authService } from "@/services/authService";
import { getLocalePreference } from "@/utils/localeStorageHelpers";
import { isValidLocaleSegment, normalizeLocaleForTranslations } from "@/utils/localeRouteHelpers";
import {
  addMediaQueryChangeListener,
  emitThemeChanged,
  getCurrentWcpTheme,
  hasAnyGrantedConsent,
  isConsentReset,
  toConsentLocale,
} from "./helpers/CookieConsentHelpers";
import type { ICookieConsentWindow, ICookieSiteConsent } from "./helpers/CookieConsentTypes";
import { useManageConsentResync } from "./helpers/useManageConsentResync";

const cookieConsentInitAttemptedLocales = new Set<string>();
const manageCookiesDialogOpenedEvent = "mci-cookie-consent-dialog-opened";

function syncUhfTheme(): void {
  const theme = document.documentElement.classList.contains("theme-dark") ? "dark" : "light";
  document.querySelectorAll("uhf-header, uhf-footer").forEach((element) => {
    element.setAttribute("theme", theme);
  });
}

/**
 * Microsoft Universal Header Component
 * Loads and renders the Microsoft UHF header with cookie consent banner
 */
export function UniversalHeader(): ReactNode {
  const params = useParams();
  const routeLocale = normalizeLocaleForTranslations(params?.locale as string | undefined);
  const storedLocale = normalizeLocaleForTranslations(getLocalePreference()?.localeCode);

  const localeCode =
    (routeLocale && isValidLocaleSegment(routeLocale) ? routeLocale : undefined) ??
    (storedLocale && isValidLocaleSegment(storedLocale) ? storedLocale : undefined) ??
    "en-us";
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const previousHasGrantedConsent = useRef<boolean | null>(null);
  const {
    armManageConsentResync,
    clearManageConsentResync,
    isManageConsentResyncRequested,
    hasObservedManageConsentChange,
  } = useManageConsentResync();
  
  useEffect(() => {
    function syncAuthenticationState(): void {
      try {
        const account = authService.getCurrentAccount();
        const authenticated = !!account;
        setIsAuthenticated(authenticated);

        if (authenticated) {
          document.body.classList.add("user-authenticated");
        } else {
          document.body.classList.remove("user-authenticated");
        }
      } catch (error) {
        console.error("Error checking auth:", error);
      }
    }

    const handleVisibilityChange = (): void => {
      if (!document.hidden) {
        syncAuthenticationState();
      }
    };

    syncAuthenticationState();
    window.addEventListener("focus", syncAuthenticationState);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    
    return (): void => {
      window.removeEventListener("focus", syncAuthenticationState);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    const handleThemeChanged = (): void => {
      syncUhfTheme();
      emitThemeChanged();
    };

    handleThemeChanged();

    const rootElement = document.documentElement;
    const observer = new MutationObserver((): void => {
      handleThemeChanged();
    });

    observer.observe(rootElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    const forcedColorsMediaQuery = window.matchMedia("(forced-colors: active)");
    const removeForcedColorsListener = addMediaQueryChangeListener(forcedColorsMediaQuery, handleThemeChanged);

    return (): void => {
      observer.disconnect();
      removeForcedColorsListener();
    };
  }, []);

  useEffect(() => {
    let cookieBannerThemeObserver: MutationObserver | null = null;

    function syncCookieBannerThemeWhenRendered(): void {
      cookieBannerThemeObserver?.disconnect();
      cookieBannerThemeObserver = null;

      const cookieBanner = document.getElementById("cookie-banner");
      if (!cookieBanner) {
        return;
      }

      if (cookieBanner.firstElementChild) {
        emitThemeChanged();
        return;
      }

      const observer = new MutationObserver((): void => {
        if (!cookieBanner.firstElementChild) {
          return;
        }

        observer.disconnect();
        cookieBannerThemeObserver = null;
        emitThemeChanged();
      });
      cookieBannerThemeObserver = observer;
      observer.observe(cookieBanner, { childList: true, subtree: true });
    }

    async function parseAndLoadUHF(): Promise<void> {
      try {
        const headerHtml = await getHeader(localeCode);

        if (!headerHtml) {
          console.error("Failed to load UHF header data");
          return;
        }

        const headerSection = document.getElementById("header-container");

        if (!headerSection) {
          console.error("Header container not found");
          return;
        }

        const header = document.getElementById("header");
        if (header) {
          header.innerHTML = replaceUhfHeaderLogoInHeaderHtml(headerHtml);
          syncUhfTheme();
          emitThemeChanged();
        }
      } catch (error) {
        console.error("Error loading UHF header:", error);
      }
    }

    async function initializeCookieConsent(forceReinitialize = false): Promise<void> {
      try {
        if (!forceReinitialize && cookieConsentInitAttemptedLocales.has(localeCode)) {
          return;
        }

        const consentLocale = toConsentLocale(localeCode);
        const windowObj = window as ICookieConsentWindow;
        const consentTheme = getCurrentWcpTheme();

        if (!windowObj.WcpConsent) {
          console.error("[CookieConsent] WcpConsent is not available on window");
          return;
        }

        if (forceReinitialize) {
          const cookieBanner = document.getElementById("cookie-banner");
          if (cookieBanner) {
            cookieBanner.innerHTML = "";
          }
        }

        cookieConsentInitAttemptedLocales.add(localeCode);

        const handleConsentChanged = (consentState: Record<string, boolean>): void => {
          const hasManageConsentChanged = hasObservedManageConsentChange(consentState);

          const hasGrantedConsent = hasAnyGrantedConsent(consentState);
          const previousState = previousHasGrantedConsent.current;
          previousHasGrantedConsent.current = hasGrantedConsent;

          if (
            isManageConsentResyncRequested() &&
            hasManageConsentChanged &&
            !hasGrantedConsent
          ) {
            clearManageConsentResync();
            void initializeCookieConsent(true);
            return;
          }

          if (isConsentReset(previousState, consentState)) {
            void initializeCookieConsent(true);
          }
        };

        let wcpTheme: "light" | "dark" | "high-contrast" = "light";
        if (consentTheme === "dark") {
          wcpTheme = windowObj.WcpConsent.themes?.dark ?? "dark";
        } else if (consentTheme === "high-contrast") {
          wcpTheme = windowObj.WcpConsent.themes?.highContrast ?? "high-contrast";
        } else {
          wcpTheme = windowObj.WcpConsent.themes?.light ?? "light";
        }

        syncCookieBannerThemeWhenRendered();
        windowObj.WcpConsent.init(
          consentLocale,
          "cookie-banner",
          (error?: unknown, siteConsent?: ICookieSiteConsent) => {
            if (error) {
              console.error("[CookieConsent] Failed to initialize:", error);
              return;
            }

            if (siteConsent) {
              windowObj.mciCookieSiteConsent = siteConsent;
              previousHasGrantedConsent.current = hasAnyGrantedConsent(siteConsent.getConsent());

              if (siteConsent.onConsentChanged) {
                siteConsent.onConsentChanged(handleConsentChanged);
              }
            }
          },
          handleConsentChanged,
          wcpTheme
        );
      } catch (error) {
        console.error("[CookieConsent] Initialization error:", error);
      }
    }

    function startManageConsentResyncWatcher(): void {
      const windowObj = window as ICookieConsentWindow;
      const initialConsentState = windowObj.mciCookieSiteConsent?.getConsent() ?? {};
      armManageConsentResync(initialConsentState);
    }

    window.addEventListener(manageCookiesDialogOpenedEvent, startManageConsentResyncWatcher);

    void (async (): Promise<void> => {
      previousHasGrantedConsent.current = null;
      await parseAndLoadUHF();
      await initializeCookieConsent();
    })();

    return (): void => {
      cookieBannerThemeObserver?.disconnect();
      clearManageConsentResync();
      window.removeEventListener(manageCookiesDialogOpenedEvent, startManageConsentResyncWatcher);
      const windowObj = window as ICookieConsentWindow;
      windowObj.mciCookieSiteConsent = undefined;
    };
  }, [
    armManageConsentResync,
    clearManageConsentResync,
    hasObservedManageConsentChange,
    isManageConsentResyncRequested,
    localeCode,
    isAuthenticated,
  ]);

  return (
    <>
      <div id="cookie-banner" role="banner" aria-label="Cookie consent banner" />
      <div id="header-container" className="header">
        <div id="header" role="banner" />
      </div>
    </>
  );
}
